import MuiAlert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import MuiStack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

/**
 * Status and progress primitives.
 *
 * Alert is the single way the application tells a user that something succeeded
 * or failed. MUI gives it role="alert" already; the `aria-live` set here
 * downgrades everything except an error to "polite", so a success message waits
 * for a pause in speech instead of interrupting. Announcing every toast
 * assertively is why some applications shout at screen-reader users.
 */

/** This application's tone names, mapped onto MUI's severities. */
const SEVERITIES = { error: 'error', success: 'success', warning: 'warning', info: 'info' };

export function Alert({ tone = 'info', title, onDismiss, dismissLabel, children, ...rest }) {
  const severity = SEVERITIES[tone] ?? 'info';

  return (
    <MuiAlert
      severity={severity}
      aria-live={severity === 'error' ? 'assertive' : 'polite'}
      onClose={onDismiss}
      slotProps={{ closeButton: { 'aria-label': dismissLabel } }}
      {...rest}
    >
      {title ? <AlertTitle>{title}</AlertTitle> : null}
      {children}
    </MuiAlert>
  );
}

/**
 * An indeterminate progress indicator.
 *
 * Purely decorative: it is aria-hidden, and whatever is actually busy carries
 * aria-busy. A spinner that announces itself competes with the message
 * explaining what is happening.
 */
export function Spinner({ size = 'md', ...rest }) {
  return <CircularProgress size={size === 'sm' ? 16 : size === 'lg' ? 40 : 24} aria-hidden="true" {...rest} />;
}

/**
 * A full-region loading state with a visible message.
 *
 * The message is what gets announced; the spinner beside it is decoration.
 */
export function LoadingState({ message, ...rest }) {
  return (
    <MuiStack
      direction="row"
      spacing={2}
      useFlexGap
      role="status"
      aria-live="polite"
      // Layout goes through sx: Stack does not forward the system props.
      sx={{ py: 8, color: 'text.secondary', alignItems: 'center', justifyContent: 'center' }}
      {...rest}
    >
      <Spinner />
      <Typography variant="body2">{message}</Typography>
    </MuiStack>
  );
}

/** A small tag, used for roles and statuses. */
const BADGE_TONES = {
  neutral: { color: 'default', variant: 'outlined' },
  primary: { color: 'primary', variant: 'filled' },
  accent: { color: 'accent', variant: 'filled' },
  success: { color: 'success', variant: 'outlined' },
  danger: { color: 'error', variant: 'outlined' }
};

export function Badge({ tone = 'neutral', children, ...rest }) {
  const mapped = BADGE_TONES[tone] ?? BADGE_TONES.neutral;

  return <Chip size="small" label={children} {...mapped} {...rest} />;
}
